import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { u, IMG } from "@/lib/site";

const EASE = [0.16, 1, 0.3, 1];


const TYPEWRITER_TEXT = "Coming Soon — Our Teak Processing Journey";

function TypewriterText() {
  const [text, setText] = useState("");
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    if (mediaQuery.matches) {
      setReduceMotion(true);
      setText(TYPEWRITER_TEXT);
      return;
    }

    let index = 0;

    const timer = window.setInterval(() => {
      index += 1;
      setText(TYPEWRITER_TEXT.slice(0, index));

      if (index >= TYPEWRITER_TEXT.length) {
        window.clearInterval(timer);
      }
    }, 55);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <span
      aria-label={TYPEWRITER_TEXT}
      className="font-display text-base font-semibold tracking-[0.06em] sm:text-lg lg:text-xl"
    >
      <span aria-hidden="true">
        {reduceMotion ? TYPEWRITER_TEXT : text}
      </span>

      {!reduceMotion && text.length < TYPEWRITER_TEXT.length && (
        <span
          className="ml-1 inline-block h-[1em] w-px translate-y-0.5 animate-pulse bg-gold"
          aria-hidden="true"
        />
      )}
    </span>
  );
}


const Masked = ({ children, delay = 0 }) => (
    <span className="-mb-1 block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const JOURNEY = [
    {
        n: "01",
        title: "Sourcing teak logs",
        img: "/teak-logs.jpg",
        alt: "Carefully selected teak round logs stacked for processing",
        text: "Every consignment begins with globally sourcing high quality teak round/rough square logs." 
          },
    {
        n: "02",
        title: "Selecting logs",
        img: "/selecting%20logs.jpeg",
        alt: "Timber moving through a professional saw-mill environment",
        text: "Sorting and selection of logs to meet client requirements with minimal wastage.",
    },
    {
        n: "03",
        title: "Precision Cutting for customized sawn size",
        img: "/precision%20sizes.jpeg",
        alt: "Precision cutting of timber into dimensional section",
        text: "Precision sawing converts each log towards exact dimensional requirement with smooth finishes to cater client need.",
    },
   ];

const RANGE = [
    {
        name: "TEAK",
        species: "Round Logs · Rough Squares · Sawn Sizes",
        img: "/teak-logs.jpg",
        alt: "Teak round logs ready for supply",
        desc: "Currently supplied in multiple forms, with customized teak processing coming soon. Length. Width. Thickness. Quantity. All Tailored to Your Requirement.",
    },
    {
        name: "SOFTWOOD",
        species: "Pine · SYP · Spruce",
        img: "/pine%20jpg.jpeg",
        alt: "Sawn pine and spruce softwood planks",
        desc: "Available in Round Logs, Sawn Sizes, Imported KD (Kiln-Dried) Planks, as well as locally Heat-Treated & Planed Timber in customized sizes. Suitable for packaging, industrial and decorative applications.",
    },
    {
        name: "HARDWOOD",
        species: "Meranti · Malaysian Sal · Wana",
        img: "/hardwood.jpeg",
        alt: "Dark hardwood timber grain close-up",
        desc: "Available in Round Logs and Sawn Sizes, selected to meet diverse commercial and application-specific requirements.",
    },
];
const SPECS = ["LENGTH", "WIDTH", "THICKNESS", "QUANTITY"];

export default function Home() {
    const { scrollY } = useScroll();
    const heroY = useTransform(scrollY, [0, 800], [0, 140]);

    return (
        <>
            <Seo
                title="ISHAAN WORLDLINK LLP | Teak, Softwood & Hardwood, Gandhidham"
                description="ISHAAN WORLDLINK LLP — Gandhidham timber enterprise supplying Teak, Softwood and Hardwood across India, backed by 20+ years of industry experience."
            />

            {/* HERO */}
            <section className="relative flex min-h-[100svh] items-end overflow-hidden bg-ink" data-testid="home-hero">
                <motion.div style={{ y: heroY }} className="absolute inset-0">
                    <img src="/teak%20front.JPG"
  alt="Teak logs and sawn timber at a professional timber processing yard"
  className="h-[112%] w-full object-cover"
  fetchPriority="high"
                    />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/25" />
                <div className="relative mx-auto w-full max-w-7xl px-4 pb-20 pt-44 sm:px-6 sm:pb-28 lg:px-8">
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.6 }}
                              data-testid="hero-badge"
                      className="mb-7 inline-flex items-center gap-3 border border-cream/25 px-5 py-3 text-base font-semibold uppercase tracking-[0.12em] text-cream/90 sm:text-lg lg:text-xl"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                         <TypewriterText />
                    </motion.p>
                    <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl">
                        <Masked delay={0.3}>CUT WITH PURPOSE.</Masked>
                        <Masked delay={0.45}>
                            <span className="text-gold">PROCESSED WITH PRECISION.</span>
                        </Masked>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.7 }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg"
                    >
                        From carefully selected teak logs to customized precision sawn sizes,
                        ISHAAN WORLDLINK LLP is taking the next step in value-added timber
                        processing.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.7 }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <Link
                            to="/products"
                            data-testid="hero-explore-btn"
                            className="group inline-flex items-center gap-2 bg-teak px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-teakdark"
                        >
                            Explore Our Timber
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="hero-enquire-btn"
                            className="inline-flex items-center gap-2 border border-cream/40 px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-cream hover:text-ink"
                        >
                            Enquire Now
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* INTRO — white */}
            <section className="bg-cream py-20 sm:py-28" data-testid="home-intro">
                <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
                    <Reveal className="lg:col-span-5">
                        <Eyebrow>Timber · Experience · Processing</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">
                            Timber expertise, shaped around your specification
                        </h2>
                    </Reveal>
                  
                    <Reveal delay={0.1} className="lg:col-span-7">
                        <p className="text-base font-normal leading-relaxed text-ink/80 sm:text-lg">
                            ISHAAN WORLDLINK LLP is a Gandhidham-based timber enterprise backed by 20+ years of{" "}
                            imported timber industry exposure and a family legacy surrounding wood spanning more than five decades.
                        </p>

                        <p className="mt-4 text-base leading-relaxed text-ink/80 sm:text-lg">
                            Our upcoming teak-processing operations in Gandhidham will focus on processing teak according to customer-specific requirements — from carefully selected logs to precision sawn sizes.
                        </p>

                        <div className="mx-auto mt-10 grid w-full max-w-5xl grid-cols-1 gap-px border border-line bg-line sm:grid-cols-3">
                            {[
                                ["20+ Years of experience", "in Imported timber market"],
                                ["Three generations & 5 Decades", "in timber profession"],
                                ["KANDLA TIMBER ASSOCIATION", "Proud member of Asia's largest timber association"],
                            ].map(([a, b]) => (
                                <div key={a} className="flex flex-col items-center justify-center bg-cream px-6 py-6 text-center">
                                    <p className="font-display text-xl font-semibold text-ink">{a}</p>
                                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-mutedw">{b}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* PROCESSING JOURNEY — warm wood */}
            <section className="bg-sand py-20 sm:py-28" data-testid="home-journey">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="mx-auto w-full max-w-5xl">
                        <Eyebrow>The Journey Ahead</Eyebrow>
                        <h2 className="mt-4 max-w-2xl font-display text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">                                                                                                                              
                          From selected teak logs to customized sawn sizes
                        </h2>
                    </Reveal>
                    <div className="mx-auto mt-14 grid w-full max-w-5xl grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
                        {JOURNEY.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.08}>
                                <div className="group" data-testid={`journey-step-${i + 1}`}>
                                    <div className="overflow-hidden">
                                        <img
                                            src={s.img.startsWith("/") ? s.img : u(s.img, 800)}
                                            alt={s.alt}
                                            loading="lazy"
                                            decoding="async"
                                            className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                        />
                                    </div>
                                    <div className="mt-5 flex items-center gap-4">
                                        <span className="font-display text-sm font-semibold text-teak">{s.n}</span>
                                        <span className="h-px flex-1 bg-ink/15" />
                                    </div>
                                    <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-ink">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-mutedw">{s.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <Marquee
                items={[
                    "TEAK",
                    "SOFTWOOD",
                    "HARDWOOD",
                    "PRECISION SAWN",
                    "GANDHIDHAM",
                    "CUSTOMER-SPECIFIC SIZES",
                ]}
            />

            {/* CUSTOMER SPECIFICATION — dark timber */}
            <section className="relative overflow-hidden bg-ink py-20 sm:py-28" data-testid="home-spec">
                <img
                    src={u(IMG.grainDark, 1600)}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.12]"
                />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <Eyebrow dark>Customer-Specific Processing</Eyebrow>
                        <h2 className="mt-4 max-w-3xl font-display text-3xl font-semibold leading-tight tracking-tight text-cream sm:text-4xl lg:text-5xl">
                            SOON, YOUR SPECIFICATION WILL BECOME OUR CUT.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-2 gap-px border border-linedark bg-linedark lg:grid-cols-4">
                        {SPECS.map((s, i) => (
                            <div key={s} className="bg-ink px-6 py-10 sm:px-8" data-testid={`spec-${s.toLowerCase()}`}>
                                <Reveal delay={i * 0.08}>
                                    <p className="font-display text-xs font-semibold tracking-[0.3em] text-gold">
                                        0{i + 1}
                                    </p>
                                    <p className="mt-3 font-display text-2xl font-semibold tracking-tight text-cream sm:text-3xl">
                                        {s}
                                    </p>
                                </Reveal>
                            </div>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <p className="mt-8 text-sm uppercase tracking-[0.25em] text-gold">
                            All Tailored to Your Requirement.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* TIMBER RANGE PREVIEW — white */}
            <section className="bg-cream py-20 sm:py-28" data-testid="home-range">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <Eyebrow>Our Timber Range</Eyebrow>
                            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">
                                Three categories. One standard.
                            </h2>
                        </div>
                        <Link
                            to="/products"
                            data-testid="range-view-all"
                            className="group inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-teak transition-colors hover:text-teakdark"
                        >
                            View all products
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                    <div className="mt-12 grid gap-6 lg:grid-cols-3">
                        {RANGE.map((c, i) => (
                            <Reveal key={c.name} delay={i * 0.08}>
                                <article
                                    className="group flex h-full flex-col border border-line bg-white"
                                    data-testid={`range-card-${c.name.toLowerCase()}`}
                                >
                                    <div className="overflow-hidden">
                                        <img
                                            src={c.img}
                                            alt={c.alt}
                                            loading="lazy"
                                            decoding="async"
                                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col p-7">
                                        <h3 className="font-display text-2xl font-semibold tracking-tight text-ink">
                                            {c.name}
                                        </h3>
                                        <p className="mt-1 text-sm font-medium text-teak">{c.species}</p>
                                        <p className="mt-4 flex-1 text-sm leading-relaxed text-mutedw">{c.desc}</p>
                                        <Link
                                            to="/products"
                                            data-testid={`range-view-${c.name.toLowerCase()}`}
                                            className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink transition-colors group-hover:text-teak"
                                        >
                                            View Products
                                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                        </Link>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA — warm wood */}
            <section className="bg-sand py-20 sm:py-24" data-testid="home-cta">
                <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <h2 className="max-w-xl font-display text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">
                            Have a specification in mind?
                        </h2>
                        <p className="mt-3 max-w-lg text-base leading-relaxed text-mutedw">
                            Share your length, width, thickness and quantity — we will take it
                            from there.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link
                            to="/contact"
                            data-testid="home-cta-enquire-btn"
                            className="group inline-flex items-center gap-2 bg-ink px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-teak"
                        >
                            Enquire Now
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
